/**
 * Funkce pro inicializaci slideru dnešních výsledků
 * Umožňuje uživateli posouvat obsah horizontálně pomocí šipek nebo drag & drop
 */
const todayResultsSlider = (results: HTMLElement) => {
  // Použijeme stejnou hodnotu breakpointu jako v CSS pro konzistenci
  const MOBILE_BREAKPOINT = 679; // odpovídá CSS media query (max-width: 679px)
  const windowWidth = window.innerWidth;
  let resizeObserver: ResizeObserver | null = null;

  // Na mobilních zařízeních skryjeme navigační šipky a ukončíme inicializaci
  if (windowWidth <= MOBILE_BREAKPOINT) {
    const slideButtons = results.querySelectorAll('[data-sportmod-slider="result-slider-btn"]');
    slideButtons.forEach((button) => {
      (button as HTMLElement).style.display = 'none';
    });
    return { cleanup: () => {} };
  }

  // Získáme hlavní wrapper, který obsahuje posuvný obsah
  const wrapper = results.querySelector(
    '[data-sportmod-slider="result-slider-wrapper"]'
  ) as HTMLElement;

  if (wrapper) {
    // Získáme navigační tlačítka (šipky) pro posun obsahu
    const slideButtons: NodeListOf<HTMLButtonElement> = results.querySelectorAll(
      '[data-sportmod-slider="result-slider-btn"]'
    );

    // Vypočítáme maximální hodnotu scrollLeft pro kontrolu konce slideru
    let maxScrollLeft = wrapper.scrollWidth - wrapper.clientWidth;

    // Získáme všechny položky slideru
    const items = results.querySelectorAll('[data-sportmod-slider="result-slider"]');

    // Nastavení pro posun po kliknutí na šipky
    const itemsPerShift = 3; // Kolik položek se přeskočí při posunu
    let currentShift = 0; // Aktuální pozice posunu
    let shiftCount = 0; // Počet provedených posunů
    const wrapperShift = wrapper.getBoundingClientRect().left; // Počáteční levá pozice wrapperu

    let currentScrollPosition = 0; // Aktuální pozice scrollu

    // Proměnné pro funkcionalitu přetahování myší (drag & drop)
    let isMouseDown = false; // Zda je myš stisknuta
    let startX = 0; // Počáteční X pozice při stisku myši
    let scrollLeft = 0; // Počáteční hodnota scrollLeft při stisku myši

    /**
     * Řídí zobrazení/skrytí navigačních šipek podle pozice scrollu
     */
    const handleSlideButtons = () => {
      // Pokud jsme na mobilním zařízení, skryjeme šipky
      if (window.innerWidth <= MOBILE_BREAKPOINT) {
        slideButtons.forEach((button) => {
          button.style.display = 'none';
        });
        return;
      }

      // Kontrola pozice scrollu pro zobrazení/skrytí šipek
      slideButtons[0].style.display = wrapper.scrollLeft <= 0 ? 'none' : 'flex'; // Levá šipka
      slideButtons[1].style.display =
        Math.ceil(wrapper.scrollLeft) >= maxScrollLeft - 1 ? 'none' : 'flex'; // Pravá šipka
    };

    /**
     * Funkce pro posun doprava - stejná logika jako při kliknutí na pravou šipku
     */
    const slideRight = () => {
      currentShift = shiftCount === 0 ? 2 : currentShift + itemsPerShift;

      if (!items[currentShift]) {
        return;
      }

      currentScrollPosition = items[currentShift].getBoundingClientRect().right - wrapperShift;
      wrapper.scrollBy({ left: currentScrollPosition, behavior: 'smooth' });

      shiftCount++;
    };

    /**
     * Funkce pro posun doleva - stejná logika jako při kliknutí na levou šipku
     */
    const slideLeft = () => {
      currentShift = currentShift === 2 ? 0 : currentShift - itemsPerShift;

      if (!items[currentShift]) {
        return;
      }

      currentScrollPosition = items[currentShift].getBoundingClientRect().left - wrapperShift;
      wrapper.scrollBy({ left: currentScrollPosition, behavior: 'smooth' });

      shiftCount--;
    };

    // Přidáme event listenery pro navigační tlačítka
    slideButtons.forEach((button) => {
      button.addEventListener('click', () => {
        // Určíme směr posunu podle ID tlačítka
        const direction = button.dataset.id === 'prev-slide' ? -1 : 1;

        if (direction === 1) {
          slideRight();
        } else {
          slideLeft();
        }
      });
    });

    /**
     * Event handler pro klávesové šipky
     * Umožňuje ovládat slider pomocí klávesových šipek stejně jako pomocí tlačítek
     */
    const handleKeyDown = (e: KeyboardEvent) => {
      // Zpracováváme pouze pokud je komponenta viditelná v aktuálním viewportu
      const rect = results.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

      if (!isVisible) {
        return;
      }

      // Ignorujeme události s modifikátory
      if (e.ctrlKey || e.altKey || e.shiftKey || e.metaKey) {
        return;
      }

      // Ignorujeme, pokud je aktivní editovatelný prvek
      if (
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement ||
        document.activeElement instanceof HTMLSelectElement
      ) {
        return;
      }

      if (e.key === 'ArrowRight') {
        // Ověříme, že nejsme na konci slideru
        if (Math.ceil(wrapper.scrollLeft) < maxScrollLeft) {
          slideRight();
          e.preventDefault();
          e.stopPropagation();
        }
      } else if (e.key === 'ArrowLeft') {
        // Ověříme, že nejsme na začátku slideru
        if (wrapper.scrollLeft > 0) {
          slideLeft();
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    // Přidáme event listener pro klávesové šipky na úrovni dokumentu
    document.addEventListener('keydown', handleKeyDown);

    /**
     * Event handler pro začátek tažení myší (mousedown)
     * Inicializuje proměnné pro sledování pohybu myši
     */
    wrapper.addEventListener('mousedown', (e) => {
      isMouseDown = true;
      wrapper.classList.add('grabbing'); // Změníme kurzor na "grabbing"

      // Zaznamenáme počáteční pozici myši a scrollu
      startX = e.clientX;
      scrollLeft = wrapper.scrollLeft;

      // Zabráníme výběru textu během tažení
      e.preventDefault();
    });

    /**
     * Event handler pro pohyb myši při tažení (mousemove)
     * Posouvá obsah na základě pohybu myši
     * Důležitá poznámka: Tento listener je na document, nikoli na wrapperu,
     * aby fungoval i když myš opustí prvek během tažení
     */
    document.addEventListener('mousemove', (e) => {
      if (!isMouseDown) return; // Pokud není myš stisknuta, nic neděláme

      // Vypočítáme, o kolik se má obsah posunout
      const deltaX = e.clientX - startX;

      // Posun obsahu - použijeme requestAnimationFrame pro lepší výkon
      requestAnimationFrame(() => {
        wrapper.scrollLeft = scrollLeft - deltaX; // Posuneme obsah
        handleSlideButtons(); // Aktualizujeme zobrazení šipek
      });

      // Zabráníme výběru textu a dalším výchozím akcím
      e.preventDefault();
    });

    /**
     * Event handler pro uvolnění myši (mouseup)
     * Ukončuje operaci tažení
     * Důležitá poznámka: Také na document, aby fungoval i když myš opustí prvek během tažení
     */
    document.addEventListener('mouseup', () => {
      if (!isMouseDown) return;

      isMouseDown = false;
      wrapper.classList.remove('grabbing'); // Vrátíme kurzor na normální
    });

    /**
     * Event handler pro opuštění prvku myší (mouseleave)
     * Dodatečné zajištění, že tažení se ukončí, když myš opustí prvek
     */
    wrapper.addEventListener('mouseleave', () => {
      if (isMouseDown) {
        isMouseDown = false;
        wrapper.classList.remove('grabbing');
      }
    });

    /**
     * Event handler pro události scrollu
     * Aktualizuje zobrazení navigačních prvků při každém posunu obsahu
     */
    wrapper.addEventListener('scroll', () => {
      handleSlideButtons();
    });

    // Počáteční nastavení navigačních prvků
    handleSlideButtons();

    /**
     * Funkce pro obsluhu změny velikosti okna
     * Aktualizuje klíčové hodnoty a zobrazení navigačních prvků
     */
    const handleResize = () => {
      // Přepočítáme maximální hodnotu scrollLeft
      maxScrollLeft = wrapper.scrollWidth - wrapper.clientWidth;

      // Kontrolujeme, zda jsme přešli na mobilní zobrazení
      if (window.innerWidth <= MOBILE_BREAKPOINT) {
        slideButtons.forEach((button) => {
          button.style.display = 'none'; // Skryjeme šipky na mobilních zařízeních
        });
      } else {
        handleSlideButtons(); // Aktualizujeme navigační prvky
      }
    };

    // Modernější metoda pro detekci změny velikosti - ResizeObserver
    if (typeof ResizeObserver !== 'undefined') {
      // Vytvoříme nový ResizeObserver, který sleduje změny velikosti prvků
      resizeObserver = new ResizeObserver(() => {
        handleResize();
      });

      // Sledujeme změny rozměrů přímo na wrapperu
      resizeObserver.observe(wrapper);

      // Také sledujeme změny v celém results kontejneru
      resizeObserver.observe(results);
    } else {
      // Fallback pro staré prohlížeče - použijeme klasický window.resize
      window.addEventListener('resize', handleResize);
    }

    /**
     * Funkce pro čištění - odstraní všechny event listenery
     * Volá se při zničení komponenty, aby nedocházelo k memory leaks
     */
    const cleanup = () => {
      // Odstranění event listenerů
      wrapper.removeEventListener('mousedown', wrapper.onmousedown as EventListener);
      document.removeEventListener('mousemove', document.onmousemove as EventListener);
      document.removeEventListener('mouseup', document.onmouseup as EventListener);
      wrapper.removeEventListener('mouseleave', wrapper.onmouseleave as EventListener);
      wrapper.removeEventListener('scroll', wrapper.onscroll as EventListener);

      // Odstranění event listeneru pro klávesnici
      document.removeEventListener('keydown', handleKeyDown);

      // Ukončení sledování změn velikosti
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', handleResize);
      }

      // Odstranění event listenerů z tlačítek
      slideButtons.forEach((button) => {
        button.removeEventListener('click', button.onclick as EventListener);
      });
    };

    // Vrátíme funkci pro čištění, která bude volána při zničení komponenty
    return { cleanup };
  }

  // Pokud wrapper neexistuje, vrátíme prázdnou cleanup funkci
  return { cleanup: () => {} };
};

export { todayResultsSlider };
